import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { FileUpload } from 'primereact/fileupload';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Sidebar } from 'primereact/sidebar';
import { useEffect, useRef, useState } from 'react';
import slugify from 'react-slugify';
import FormInput from '../../../../components/Form/FormInput';
import RichTextEditor from '../../../../components/Form/RichTextEditor';
import LanguageManagerGuard from '../../../../components/Language/LanguageManagerGuard';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { useToastContext } from '../../../../contexts/ToastContext';
import { privateFetch } from '../../../../utils/apiHelper';
import { CONSTANT } from '../../../../utils/constants';

function ProjectPanel({ isOpen, onClose, project, loading, onUpdate, onCreate }) {
  const [title, setTitle] = useState('');
  const [year, setYear] = useState(0);
  const [isHighlight, setIsHighlight] = useState(false);
  const [description, setDescription] = useState('');
  const [review, setReview] = useState('');
  const [mainImage, setMainImage] = useState(undefined);
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [surface, setSurface] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const { showToast } = useToastContext();
  const fileUploadRef = useRef(null);

  const { accountContext } = useAccountContext();

  useEffect(() => {
    if (project) {
      setTitle(project.title);
      setYear(project.year);
      setIsHighlight(project.is_highlight);
      setDescription(project.description);
      setReview(project.review);
      setMainImage(project.main_image);
      setCountry(project.country);
      setCity(project.city);
      setSurface(project.surface);
    } else {
      setTitle('');
      setYear(0);
      setIsHighlight(false);
      setDescription('');
      setMainImage(undefined);
      setCountry('');
      setCity('');
      setSurface(null);
      setReview('');
    }
  }, [project, isOpen]);

  const uploadImage = async (imageForm) => {
    await privateFetch(
      'POST',
      'resources',
      `/cu/${accountContext.id}/large-image/`,
      "L'image a bien été ajoutée",
      imageForm,
      false,
      true
    ).then((res) => {
      setMainImage(res);
    });
    setImageLoading(false);
  };

  const onUpload = (event) => {
    if (event.files[0].size > CONSTANT.maxImageSize) {
      showToast('warn', '10Mo max.');
      onClear();
    } else {
      setImageLoading(true);

      const file = event.files[0];
      const imageForm = new FormData();
      imageForm.append('title', slugify(file.title));
      imageForm.append('file', file);
      uploadImage(imageForm);
    }
  };

  const onClear = () => {
    setMainImage(undefined);
    fileUploadRef.current.clear();
  };

  const handleValidate = () => {
    const projectPayload = {
      title,
      year,
      is_highlight: isHighlight,
      description,
      country,
      city,
      surface,
      main_image: mainImage?.id,
      review
    };
    project ? onUpdate(projectPayload) : onCreate(projectPayload);
  };

  const formIsValid = () => {
    return title && mainImage && year && country && city;
  };

  const footerButtons = (
    <div className="manage-footer">
      <Button
        key={'validate'}
        label={`Valider la ${project ? 'modification' : 'création'}`}
        className="w-full"
        onClick={handleValidate}
        severity="success"
        disabled={!formIsValid()}
        loading={loading}
      />
    </div>
  );

  return (
    <Sidebar
      style={{ width: 700 }}
      visible={isOpen}
      position="right"
      onHide={() => onClose(false)}
      header={
        <h3 className="text-slate-800 text-2xl font-medium">{`${project ? 'Modifier' : 'Ajouter'} un projet`}</h3>
      }>
      <LanguageManagerGuard isCreation={!project}>
        <div className="sidebar-content-container">
          <div className="form-wrapper !pb-60">
            <FormInput label={'Titre du projet'} required isValid={title.length} translatable>
              <InputText
                id="title"
                value={title}
                maxLength={300}
                placeholder="Veuillez saisir le titre du projet"
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormInput>
            <FormInput label={'Année'} required isValid={year}>
              <InputNumber
                id="year"
                value={year}
                placeholder="Veuillez saisir l'année"
                onChange={(e) => setYear(e.value)}
                locale="fr"
              />
            </FormInput>
            <FormInput label={'Pays'} required isValid={country.length} translatable>
              <InputText
                id="country"
                value={country}
                maxLength={300}
                placeholder="Veuillez saisir le pays"
                onChange={(e) => setCountry(e.target.value)}
              />
            </FormInput>
            <FormInput label={'Ville'} required isValid={city.length} translatable>
              <InputText
                id="city"
                value={city}
                maxLength={300}
                placeholder="Veuillez saisir la ville"
                onChange={(e) => setCity(e.target.value)}
              />
            </FormInput>

            <FormInput
              key={'file'}
              label={`Photo principale (max. 10Mo)`}
              required
              isValid={mainImage}>
              {mainImage && (
                <img
                  src={mainImage.file}
                  className="max-w-[400px] max-h-[400px] object-contain object-left mb-5"
                />
              )}
              <div className="flex flex-row gap-x-3">
                {mainImage && (
                  <Button
                    key={'delete-img'}
                    label={"Supprimer l'image"}
                    outlined
                    onClick={onClear}
                    severity="danger"
                  />
                )}
                {imageLoading ? (
                  <div className="w-[50px] h-[50px] p-1">
                    <ProgressSpinner style={{ width: '42px', height: '42px' }} />
                  </div>
                ) : (
                  <FileUpload
                    ref={fileUploadRef}
                    auto
                    disabled={mainImage}
                    mode="basic"
                    title="file"
                    accept="image/*"
                    customUpload
                    uploadHandler={onUpload}
                    chooseLabel="Depuis mon ordinateur"
                  />
                )}
              </div>
            </FormInput>

            <Divider />

            <FormInput
              label={`${isHighlight ? 'Le projet est mis en avant' : "Le projet n'est pas mis en avant"}`}>
              <InputSwitch checked={isHighlight} onChange={(e) => setIsHighlight(e.value)} />
            </FormInput>

            <div className={`flex flex-col gap-5 ${!isHighlight && 'opacity-30'} `}>
              <FormInput label={'Surface en m²'}>
                <InputNumber
                  id="surface"
                  value={surface}
                  placeholder="Veuillez saisir la surface"
                  onChange={(e) => setSurface(e.value)}
                  locale="fr"
                />
              </FormInput>

              <FormInput label={'Description'} translatable>
                <RichTextEditor
                  value={description}
                  onChange={(value) => {
                    setDescription(value);
                  }}
                  readOnly={!isHighlight}
                />
              </FormInput>
              <FormInput label={'Avis Client'} translatable>
                <RichTextEditor
                  value={review}
                  onChange={(value) => {
                    setReview(value);
                  }}
                  readOnly={!isHighlight}
                />
              </FormInput>
            </div>
          </div>
          {footerButtons}
        </div>
      </LanguageManagerGuard>
    </Sidebar>
  );
}

export default ProjectPanel;
